import type { ReactNode } from "react";
import { useId } from "react";
import { cn } from "../cn";

/**
 * The console's charts.
 *
 * Hand-drawn SVG rather than a charting library, and the reasoning is recorded
 * here because "why is there no Recharts" is the first question a reader will
 * have. Recharts clears the licence gate in `0009` — it and its whole
 * transitive tree are MIT or ISC, with no copyleft and no resale restriction —
 * so this is a merit decision, not a licence one. It brings Redux Toolkit,
 * react-redux, immer and the d3 scale/shape/array packages into the bundle to
 * draw two shapes, and its `ResponsiveContainer` measures zero in jsdom, which
 * would mean every test below either mocks a ResizeObserver or asserts nothing.
 * Two fixed-viewBox SVGs cost less than that and test as ordinary DOM. If
 * charts later grow brushing, zoom or stacked series, Recharts is the vetted
 * answer and this trade should be revisited rather than extended.
 *
 * Colour comes from tokens only. Not a single literal is written here, because
 * a hex in a chart is a colour that resolves in exactly one theme and looks
 * fine in review — the light/dark split shows up as unreadable data on somebody
 * else's machine, weeks later.
 */

/** Which series colour a mark takes. `1` is the subject of a single-series chart. */
export type ChartSeries = 1 | 2 | 3 | 4 | 5;

/**
 * The series colours, as the custom properties themselves.
 *
 * Written as `var(…)` strings rather than Tailwind classes because a gradient
 * stop needs the value, not a utility, and because a class name assembled from
 * a variable is invisible to Tailwind's scanner and would be dropped from the
 * build.
 */
const SERIES: Record<ChartSeries, string> = {
  1: "var(--color-chart-1)",
  2: "var(--color-chart-2)",
  3: "var(--color-chart-3)",
  4: "var(--color-chart-4)",
  5: "var(--color-chart-5)",
};

export interface ChartPoint {
  /** How this point is named to a person — a time, a day, a bucket. */
  label: string;
  value: number;
}

export interface TrendChartProps {
  points: ChartPoint[];
  /**
   * What the chart is, for anyone who cannot see it. An unlabelled graphic is
   * announced as "image" and carries nothing, which is worse than absent.
   */
  label: string;
  series?: ChartSeries;
  /** Formats the value in the summary and the peak marker. */
  formatValue?: (value: number) => string;
  className?: string;
}

// A fixed viewBox scaled by CSS, rather than a measured container. The chart
// keeps its aspect ratio, stroke widths and the endpoint marker scale with it,
// and there is no ResizeObserver to fail on a server render or in a test.
const W = 640;
const H = 160;
const PAD_TOP = 10;
const PAD_BOTTOM = 10;
const PAD_RIGHT = 8;

/**
 * One series over time, as an area.
 *
 * The y-axis starts at zero and is never truncated. These charts count things —
 * events arriving, rewards given — and a counted quantity on a clipped axis
 * turns an ordinary week into a cliff, which is the single most common way a
 * chart lies without anybody intending it to.
 *
 * Straight segments, no smoothing: a spline through counted values invents
 * readings between the points that were never measured.
 */
export function TrendChart({
  points,
  label,
  series = 1,
  formatValue = (v) => String(v),
  className,
}: TrendChartProps) {
  // `useId` is not guaranteed to be a valid bare identifier — React wraps it in
  // punctuation that varies by version — and it is referenced here from a
  // `url(#…)`, so strip it down to something a fragment reference cannot
  // misparse.
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
  const fillId = `chart-fill-${uid}`;
  const colour = SERIES[series];

  const values = points.map((p) => p.value);
  const peak = values.length > 0 ? Math.max(...values) : 0;
  const total = values.reduce((sum, v) => sum + v, 0);
  const last = points.at(-1);

  const x = (i: number) =>
    points.length > 1 ? (i / (points.length - 1)) * (W - PAD_RIGHT) : (W - PAD_RIGHT) / 2;
  // A flat run of zeroes still has to draw a line rather than divide by zero,
  // so an all-zero series sits on the baseline instead of at the top.
  const y = (value: number) =>
    peak === 0 ? H - PAD_BOTTOM : H - PAD_BOTTOM - (value / peak) * (H - PAD_TOP - PAD_BOTTOM);

  const line = points.map((p, i) => `${i === 0 ? "M" : "L"}${x(i)},${y(p.value)}`).join(" ");
  const area = points.length > 0 ? `${line} L${x(points.length - 1)},${H} L${x(0)},${H} Z` : "";

  const summary =
    points.length === 0
      ? `${label}. Nothing to show yet.`
      : `${label}. ${formatValue(total)} in total across ${points.length} points, ` +
        `highest ${formatValue(peak)}, from ${points[0]?.label ?? ""} to ${last?.label ?? ""}.`;

  return (
    <figure className={cn("m-0", className)}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="h-auto w-full overflow-visible"
        role="img"
        aria-label={summary}
      >
        <defs>
          <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
            {/* The fill is the same colour as the line, faded, so the area
                reads as the line's own weight rather than as a second series. */}
            <stop offset="0%" style={{ stopColor: colour, stopOpacity: 0.3 }} />
            <stop offset="100%" style={{ stopColor: colour, stopOpacity: 0.02 }} />
          </linearGradient>
        </defs>

        {/* Drawn behind everything and deliberately faint — a reading aid, not
            a border. */}
        {[0, 0.25, 0.5, 0.75, 1].map((fraction) => {
          const gy = PAD_TOP + fraction * (H - PAD_TOP - PAD_BOTTOM);
          return (
            <line
              key={fraction}
              x1={0}
              x2={W}
              y1={gy}
              y2={gy}
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
              style={{ stroke: "var(--color-chart-grid)" }}
            />
          );
        })}

        {points.length > 0 ? (
          <>
            <path d={area} fill={`url(#${fillId})`} />
            <path
              d={line}
              fill="none"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
              style={{ stroke: colour }}
            />
            {/* The newest point, emphasised. On a chart answering "is anything
                arriving", the right-hand end is the answer and everything to
                the left is context — so it gets a marker, ringed in the
                surface colour so it stays legible over the area beneath it. */}
            {last ? (
              <circle
                cx={x(points.length - 1)}
                cy={y(last.value)}
                r={4}
                strokeWidth={3}
                vectorEffect="non-scaling-stroke"
                style={{ fill: colour, stroke: "var(--color-surface)" }}
                data-testid="trend-endpoint"
              />
            ) : null}
          </>
        ) : null}
      </svg>

      {points.length > 0 ? (
        <figcaption className="mt-2 flex items-baseline justify-between font-sans text-meta text-ink-3 tabular-nums">
          <span>{points[0]?.label}</span>
          <span className="text-ink-2">
            highest {formatValue(peak)}
            {last ? ` · latest ${formatValue(last.value)}` : null}
          </span>
          <span>{last?.label}</span>
        </figcaption>
      ) : null}
    </figure>
  );
}

export interface BarRow {
  id: string;
  /** What a person calls this. Never an identifier on its own. */
  label: ReactNode;
  value: number;
  series?: ChartSeries;
  /** Secondary text under the label — a trust tier, an identifier, a note. */
  meta?: ReactNode;
}

export interface BarChartProps {
  rows: BarRow[];
  /** Names the group. Rendered to assistive technology, not on screen. */
  label: string;
  formatValue?: (value: number) => string;
  className?: string;
}

/**
 * A ranked breakdown, as proportion bars.
 *
 * Every value is real text in the DOM and the bars are decorative, which is the
 * opposite arrangement from `TrendChart` and the better one where it is
 * available: nobody has to trust a generated summary, because the numbers are
 * simply readable. Bars are drawn against the largest row rather than the sum,
 * so the longest bar always fills its track and the comparison is between rows
 * rather than against a total that may not be meaningful.
 */
export function BarChart({
  rows,
  label,
  formatValue = (v) => String(v),
  className,
}: BarChartProps) {
  const peak = rows.reduce((max, r) => Math.max(max, r.value), 0);

  return (
    <ul aria-label={label} className={cn("space-y-3", className)}>
      {rows.map((row) => {
        const colour = SERIES[row.series ?? 1];
        const width = peak === 0 ? 0 : (row.value / peak) * 100;
        return (
          <li key={row.id}>
            <div className="flex items-baseline justify-between gap-4">
              <span className="min-w-0 text-ui text-ink">{row.label}</span>
              <span className="shrink-0 font-sans text-ui font-medium text-ink tabular-nums">
                {formatValue(row.value)}
              </span>
            </div>
            <div
              aria-hidden="true"
              className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-sunk"
            >
              <div
                className="h-full rounded-full"
                style={{ width: `${width}%`, backgroundColor: colour }}
              />
            </div>
            {row.meta ? <div className="mt-1.5">{row.meta}</div> : null}
          </li>
        );
      })}
    </ul>
  );
}
